import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {ResourceDisplayKind} from "crqlar-react-common/dist/models/aggregation/aggregation.model";

export const getValidDateTimeValue = (value: string | Date | undefined): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);

    if (!date || Number.isNaN(date.getTime())) {
        return undefined;
    }

    return date;
};

const VISUALLY_INACTIVE_RESERVATION_STATUSES = new Set<ReservationStatus>([
    ReservationStatus.CANCELLED,
    ReservationStatus.NO_SHOW,
    ReservationStatus.COMPLETED,
]);

export const isReservationVisuallyInactive = (
    status: ReservationStatus | undefined,
    start: string | Date | undefined,
    end: string | Date | undefined,
    now: Date,
) => {
    const endDate = getValidDateTimeValue(end);
    const startDate = getValidDateTimeValue(start);
    const inactiveAt = endDate ?? startDate;
    return Boolean(
        (inactiveAt && inactiveAt.getTime() <= now.getTime())
        || (status && VISUALLY_INACTIVE_RESERVATION_STATUSES.has(status)),
    );
};

export const getLocalizedValue = (
    localizations: Record<string, string> | undefined,
    locale: string
): string | undefined => {
    if (!localizations) {
        return undefined;
    }

    // Try exact locale match (e.g. "de")
    const match = localizations[locale];
    if (match) {
        return match;
    }

    // Try 2-letter fallback (e.g. if locale is "de-AT")
    const shortLocale = locale.split("-")[0];
    const shortMatch = localizations[shortLocale];
    if (shortMatch) {
        return shortMatch;
    }

    // Fall back to the first available translation
    return Object.values(localizations).find(Boolean);
};

// --- Raw resource classification ---------------------------------------------
// Aggregation responses carry their server-owned display kind. This matching helper is only for
// unsaved/native Resource models used by the agenda and resource editor.

const STAFF_NAME_TOKENS = ['staff', 'mitarbeiter'];
const FACILITY_NAME_TOKENS = ['facilit'];

const categoryNameLooksLikeStaff = (
    category: Pick<ResourceCategory, 'name' | 'nameLocalized'>
) => {
    const names = [category.name, ...Object.values(category.nameLocalized ?? {})];
    return names.some(name => {
        const normalizedName = (name ?? '').toLowerCase();
        return STAFF_NAME_TOKENS.some(token => normalizedName.includes(token));
    });
};

const categoryNameLooksLikeFacility = (
    category: Pick<ResourceCategory, 'name' | 'nameLocalized'>
) => {
    const names = [category.name, ...Object.values(category.nameLocalized ?? {})];
    return names.some(name => {
        const normalizedName = (name ?? '').toLowerCase();
        return FACILITY_NAME_TOKENS.some(token => normalizedName.includes(token));
    });
};

export const collectStaffCategoryIds = (categories: ResourceCategory[]) => new Set(
    categories
        .filter(category => category.id && categoryNameLooksLikeStaff(category))
        .map(category => category.id as string)
);

export const isStaffResource = (resource: Resource, staffCategoryIds: ReadonlySet<string>) =>
    (resource.categoryIds ?? []).some(categoryId => staffCategoryIds.has(categoryId));

export const getResourceDisplayKind = (
    resource: Resource,
    sectorsById: ReadonlyMap<string, Sector>,
    categoriesById: ReadonlyMap<string, ResourceCategory>
): ResourceDisplayKind => {
    const sectorTypes = new Set(
        (resource.sectorIds ?? [])
            .map(sectorId => sectorsById.get(sectorId)?.sectorType)
            .filter((sectorType): sectorType is SectorType => !!sectorType)
    );

    if (sectorTypes.has(SectorType.RESTAURANT)) {
        return resource.parentId
            ? ResourceDisplayKind.RESTAURANT_TABLE
            : ResourceDisplayKind.RESTAURANT;
    }
    if (sectorTypes.has(SectorType.ACCOMMODATION)) return ResourceDisplayKind.ACCOMMODATION;

    const categories = (resource.categoryIds ?? []).map(id => categoriesById.get(id)).filter(Boolean);
    if (sectorTypes.has(SectorType.SPA)) {
        return categories.some(category => categoryNameLooksLikeFacility(category!))
            ? ResourceDisplayKind.FACILITY
            : ResourceDisplayKind.STAFF;
    }
    return categories.some(category => categoryNameLooksLikeStaff(category!))
        ? ResourceDisplayKind.STAFF
        : ResourceDisplayKind.FACILITY;
};
